import React, { useEffect, useRef, useState } from 'react'

import DOMPurify from 'dompurify'

import sendRequest from '../../services/api/sendRequest'
import Permissions, { CAPABILITIES } from '../../utils/Permissions'
import FAIcon from '../FAIcon'

// Helper for detecting empty Quill rich-text markup per AGENTS.md rules
export function isQuillEmpty(html) {
  if (!html) return true
  const stripped = html.replace(/<[^>]*>/g, '').trim()
  return stripped.length === 0
}

// Parse spoken duration phrases into 0.1-hr increments
export function parseSpokenHours(textFragment) {
  if (!textFragment) return 0
  const s = textFragment.toLowerCase().trim()

  const wordMap = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    ten: 10,
    fifteen: 15,
    twenty: 20,
    thirty: 30,
    forty: 40,
    'forty-five': 45,
    'forty five': 45,
    fifty: 50,
    sixty: 60,
    ninety: 90,
  }

  if (s.includes('an hour and a half') || s.includes('hour and a half')) return 1.5
  if (s.includes('half an hour') || s.includes('half hour')) return 0.5
  if (s.includes('quarter of an hour') || s.includes('quarter hour')) return 0.3
  if (s === 'an hour' || s === 'one hour' || s === '1 hour') return 1.0

  const hrMinMatch = s.match(/(\d+)\s*(?:hours?|hrs?)\s*(?:and\s*)?(\d+)\s*(?:minutes?|mins?)/)
  if (hrMinMatch) {
    const hrs = parseFloat(hrMinMatch[1]) || 0
    const mins = parseFloat(hrMinMatch[2]) || 0
    return Math.max(0.1, Math.ceil((hrs + mins / 60) * 10 - 1e-9) / 10)
  }

  const decHrMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?)/)
  if (decHrMatch) {
    const hrs = parseFloat(decHrMatch[1]) || 0
    return hrs > 0 ? Math.max(0.1, Math.ceil(hrs * 10 - 1e-9) / 10) : 0
  }

  const minMatch = s.match(/(\d+)\s*(?:minutes?|mins?)/)
  if (minMatch) {
    const mins = parseFloat(minMatch[1]) || 0
    return mins > 0 ? Math.max(0.1, Math.ceil((mins / 60) * 10 - 1e-9) / 10) : 0
  }

  for (const [word, val] of Object.entries(wordMap)) {
    if (s.includes(`${word} min`)) {
      return Math.max(0.1, Math.ceil((val / 60) * 10 - 1e-9) / 10)
    }
    if (s.includes(`${word} hour`) || s.includes(`${word} hr`)) {
      return Math.max(0.1, Math.ceil(val * 10 - 1e-9) / 10)
    }
  }

  return 0
}

// Synthesize a concise, action-oriented task title (3-8 words) from a spoken clause
// so raw transcription sentences never become the task title.
export function summarizeTaskTitle(rawText) {
  if (!rawText) return ''
  let clause = String(rawText).replace(/\s+/g, ' ').trim()

  // If the text is a long run-on sentence starting with visit/travel narrative, isolate the actionable clause
  const actionStartMatch = clause.match(
    /\b(?:i\s+need\s+to|we\s+need\s+to|need\s+to|needs\s+to|must|please|request|obtain|get\s+authorization|authorize|schedule|order|submit|send|transmit|follow\s+up\s+(?:on|to|with)|coordinate|refer|recommend(?:ed)?\s+\d+|requires?\s+\d+|medication\s+dosage\s+review)\b[\s\S]*$/i,
  )
  if (actionStartMatch && (clause.length > 50 || /^(?:attended|spent|travel|conferred\s+with)\b/i.test(clause))) {
    clause = actionStartMatch[0].trim()
  }

  // Strip trailing non-task clauses (e.g., "and conferred with...", "spent 1.2 hours...", "next visit in...")
  clause = clause
    .replace(/\b(?:and\s+)?(?:conferred\s+with|spent\s+\d|travel\s+was|next\s+visit\s+in)\b[\s\S]*$/i, '')
    .replace(/[.!?]+$/, '')
    .trim()

  const lower = clause.toLowerCase()

  // High-confidence clinical task title patterns
  if (/\b(physical therapy|pt)\b/i.test(lower)) {
    if (/\b(auth|authorization|pre-auth|extension)\b/i.test(lower)) {
      return 'Request PT Authorization'
    }
    return 'Coordinate Physical Therapy'
  }
  if (/\b(occupational therapy|ot)\b/i.test(lower)) {
    if (/\b(auth|authorization|pre-auth|extension)\b/i.test(lower)) {
      return 'Request OT Authorization'
    }
    return 'Coordinate Occupational Therapy'
  }
  if (/\b(mri|ct\s+scan|x-?ray|ultrasound|emg|diagnostic|imaging)\b/i.test(lower)) {
    const modMatch = clause.match(/\b(MRI|CT|X-?Ray|EMG|Ultrasound)\b/i)
    const modLabel = modMatch ? modMatch[1].toUpperCase() : 'Diagnostic Imaging'
    if (/\b(auth|authorization|pre-auth)\b/i.test(lower)) {
      return `Request ${modLabel} Authorization`
    }
    if (/\b(result|results|report|review|films)\b/i.test(lower)) {
      return `Follow Up on ${modLabel} Results`
    }
    return `Schedule ${modLabel}`
  }
  if (/\b(medical records?|chart notes?|records?\s+request|office notes?)\b/i.test(lower)) {
    return 'Request Medical Records'
  }
  if (
    /\b(medication|dosage|prescription|pharmacy|refill)\b/i.test(lower) &&
    /\b(review|check|reconcil|auth)\b/i.test(lower)
  ) {
    return 'Medication Dosage Review'
  }
  if (
    /\b(work status|restrictions?|duty)\b/i.test(lower) &&
    /\b(employer|adjuster|carrier|send|transmit|notify|forward)\b/i.test(lower)
  ) {
    return 'Send Work Status Update'
  }
  if (/\b(refer|referral|specialist|consult)\b/i.test(lower)) {
    return 'Coordinate Specialist Referral'
  }

  // General cleanup: strip conversational lead-ins and normalize verb
  let cleaned = clause
    .replace(
      /^(?:and\s+|also\s+|then\s+)?(?:i\s+need\s+to|we\s+need\s+to|need\s+to|needs\s+to|i\s+must|we\s+must|must|i\s+have\s+to|we\s+have\s+to|please|make\s+sure\s+to|remember\s+to|patient\s+(?:needs|requires)|doctor\s+(?:recommended|ordered|wants)|dr\.?\s+[a-z]+\s+(?:recommended|ordered|wants))\s+/i,
      '',
    )
    .replace(/^get\s+/i, 'Obtain ')
    .replace(/[.!?]+$/, '')
    .trim()

  if (!cleaned) {
    cleaned = clause
  }

  // Cap at 8 words / 60 chars so it reads as a task title, never a raw transcript dump
  const words = cleaned.split(/\s+/)
  if (words.length > 8) {
    cleaned = words
      .slice(0, 8)
      .join(' ')
      .replace(/[,;:-]+$/, '')
  }
  if (cleaned.length > 60) {
    cleaned = cleaned.slice(0, 57).trim()
  }

  if (!cleaned) return ''

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

// Local clinical entity parser so any dictated or edited transcript extracts immediately
export function parseClinicalEntitiesFromTranscript(rawTranscript) {
  const clean = (rawTranscript || '').trim()
  if (!clean) {
    return {
      appointmentNotes: '',
      billableActivities: [],
      workStatusUpdate: null,
      proposedTasks: [],
      nextAppointment: null,
      contacts: [],
    }
  }

  const lower = clean.toLowerCase()

  // 1. Appointment Notes
  const appointmentNotes = `<p>${clean}</p>`

  // 2. Billable Activities
  const billableActivities = []
  const sentences = clean.split(/(?<=[.!?])\s+|\n+/)

  sentences.forEach(sentence => {
    const sLower = sentence.toLowerCase()
    // Check if sentence mentions travel/drive
    if (/\b(travel|drove|driving|round trip|mileage|commute)\b/i.test(sLower)) {
      const time = parseSpokenHours(sLower) || 0.5
      billableActivities.push({
        type: 'Travel',
        description: sentence.replace(/\.$/, '').trim(),
        time,
      })
    } else if (
      /\b(phone|called|spoke with|talked to|conferred|telephonic)\b/i.test(sLower) &&
      parseSpokenHours(sLower) > 0
    ) {
      billableActivities.push({
        type: 'Phone Call',
        description: sentence.replace(/\.$/, '').trim(),
        time: parseSpokenHours(sLower),
      })
    } else if (
      /\b(reviewed|record review|medical records|imaging review)\b/i.test(sLower) &&
      parseSpokenHours(sLower) > 0
    ) {
      billableActivities.push({
        type: 'Record Review',
        description: sentence.replace(/\.$/, '').trim(),
        time: parseSpokenHours(sLower),
      })
    } else if (/\b(spent|attended|appointment|visit|clinic|exam|met with|consult)\b/i.test(sLower)) {
      const time = parseSpokenHours(sLower)
      if (time > 0) {
        billableActivities.push({
          type: 'Coordination of Services',
          description: sentence.replace(/\.$/, '').trim(),
          time,
        })
      }
    }
  })

  // If sentence contained both clinic time and travel time separated by comma (e.g. "Spent 1.2 hours at the clinic, travel was 0.8 hours round trip")
  const clinicClause = lower.match(
    /(?:spent|attended|waited)\s+([^,.]+?)\s+(?:at the clinic|in clinic|at clinic|with patient|in exam)/i,
  )
  const travelClause = lower.match(/travel\s+(?:was\s+|time\s+)?([^,.]+?)(?:\s+round trip|\.|,|$)/i)
  if (clinicClause && travelClause) {
    const clinicTime = parseSpokenHours(clinicClause[1])
    const travelTime = parseSpokenHours(travelClause[1])
    if (clinicTime > 0 && travelTime > 0) {
      billableActivities.length = 0
      billableActivities.push(
        {
          type: 'Coordination of Services',
          description: 'Clinic Attendance & Care Coordination',
          time: clinicTime,
        },
        {
          type: 'Travel',
          description: 'Round trip travel to clinic',
          time: travelTime,
        },
      )
    }
  } else if (billableActivities.length === 0) {
    const anyTime = parseSpokenHours(lower)
    if (anyTime > 0) {
      billableActivities.push({
        type: 'Coordination of Services',
        description: 'Case management clinical coordination',
        time: anyTime,
      })
    }
  }

  // 3. Work Status Update
  let workStatusUpdate = null
  const todayIso = new Date().toISOString().slice(0, 10)
  const restrictionMatch = clean.match(
    /(?:no lifting[^.;,]+|lifting limit[^.;,]+|no prolonged[^.;,]+|restrictions?[:\s]+[^.;]+|with no [^.;]+)/i,
  )
  if (/\b(light duty|modified duty|transitional duty|sedentary duty)\b/i.test(lower)) {
    workStatusUpdate = {
      workStatus: 'Modified duty',
      restrictions: restrictionMatch ? restrictionMatch[0].trim() : 'Modified / Light duty per physician',
      effectiveDate: todayIso,
    }
  } else if (/\b(full duty|regular duty|cleared for full|released to full)\b/i.test(lower)) {
    workStatusUpdate = {
      workStatus: 'Full duty',
      restrictions: 'None (Full duty release)',
      effectiveDate: todayIso,
    }
  } else if (/\b(off work|unable to work|temporarily disabled|total temporary disability|ttd)\b/i.test(lower)) {
    workStatusUpdate = {
      workStatus: 'Off work',
      restrictions: restrictionMatch ? restrictionMatch[0].trim() : 'Off work per physician order',
      effectiveDate: todayIso,
    }
  }

  // 4. Proposed Follow-Up Tasks
  const proposedTasks = []
  const taskClauses =
    sentences.length === 1 && clean.length > 70
      ? clean.split(
          /(?:,\s*and\s+|\s+and\s+(?=(?:i\s+need|we\s+need|need\s+to|please|request|obtain|schedule|follow\s+up)))/i,
        )
      : sentences

  taskClauses.forEach(sentence => {
    const sLower = sentence.toLowerCase().trim()
    if (
      /^(attended|spent|travel|conferred with)\b/i.test(sLower) &&
      !/\b(need to|needs to|request|obtain|authorization|pre-auth|authorize|schedule|order|submit)\b/i.test(sLower)
    ) {
      return
    }
    if (
      /\b(need to|needs to|needed|needs|require|requires|required|must|request|obtain|authorization|pre-auth|authorize|schedule|order|submit|send|transmit|follow up on|follow up to|coordinate|refer|referral|recommend|recommended|review)\b/i.test(
        sLower,
      ) ||
      /\b(additional\s+[^.]+?\b(?:sessions?|visits?|weeks?)|physical therapy|occupational therapy)\b/i.test(sLower)
    ) {
      let leadDays = 3
      if (/\b(work status|employer|restrictions)\b/i.test(sLower)) leadDays = 1
      else if (/\b(specialist|consult|records|surgical|ur)\b/i.test(sLower)) leadDays = 5
      const dueDateObj = new Date(Date.now() + leadDays * 86400000)
      const due = dueDateObj.toISOString().slice(0, 10)
      const conciseTitle = summarizeTaskTitle(sentence)
      if (conciseTitle.length > 3 && !proposedTasks.some(pt => pt.title.toLowerCase() === conciseTitle.toLowerCase())) {
        proposedTasks.push({
          title: conciseTitle,
          description: sentence.trim(),
          dueDate: due,
          dueDateUtc: `${due}T00:00:00.000Z`,
        })
      }
    }
  })

  // 5. Next Appointment
  let nextAppointment = null
  const followUpMatch = clean.match(
    /(?:next visit|follow[- ]?up|return to clinic|re-?evaluate|see (?:him|her|patient|dr\.?[^.,]+))\s+(?:in\s+)?(\d+)\s*(weeks?|days?|months?)/i,
  )
  const drMatch = clean.match(/\b(Dr\.?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/)
  if (followUpMatch) {
    const count = parseInt(followUpMatch[1], 10) || 4
    const unit = followUpMatch[2].toLowerCase()
    const daysToAdd = unit.startsWith('week') ? count * 7 : unit.startsWith('month') ? count * 30 : count
    const suggestedDate = new Date(Date.now() + daysToAdd * 86400000).toISOString().slice(0, 10)
    nextAppointment = {
      title: 'Follow-Up Evaluation',
      provider: drMatch ? drMatch[1] : '',
      date: suggestedDate,
      suggestedDate,
      description: followUpMatch[0],
    }
  } else if (/\b(next visit|follow[- ]?up appointment|next appointment)\b/i.test(lower)) {
    const suggestedDate = new Date(Date.now() + 28 * 86400000).toISOString().slice(0, 10)
    nextAppointment = {
      title: 'Follow-Up Evaluation',
      provider: drMatch ? drMatch[1] : '',
      date: suggestedDate,
      suggestedDate,
      description: 'Scheduled follow-up evaluation',
    }
  }

  // 6. Key Contacts
  const contacts = []
  const extMatch = clean.match(/(?:extension|ext\.?)\s*(\d{2,5})/i)
  const phoneMatch = clean.match(/(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/)
  const staffMatch = clean.match(
    /(?:medical assistant|assistant|ma|coordinator|adjuster|nurse|therapist|clerk)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
  )
  if (staffMatch) {
    const roleMatch = staffMatch[0].match(
      /^(medical assistant|assistant|ma|coordinator|adjuster|nurse|therapist|clerk)/i,
    )
    contacts.push({
      name: staffMatch[1],
      role: roleMatch ? roleMatch[1].replace(/\b\w/g, c => c.toUpperCase()) : 'Clinic Staff',
      extension: extMatch ? extMatch[1] : null,
      phone: phoneMatch ? phoneMatch[1] : null,
      organizationOrProviderName: drMatch ? drMatch[1] : null,
    })
  } else if (drMatch) {
    contacts.push({
      name: drMatch[1],
      role: 'Physician',
      extension: extMatch ? extMatch[1] : null,
      phone: phoneMatch ? phoneMatch[1] : null,
    })
  }

  return {
    transcript: clean,
    appointmentNotes,
    billableActivities,
    workStatusUpdate,
    proposedTasks,
    nextAppointment,
    contacts,
  }
}

const DEFAULT_EXTRACTED_ENTITIES = {
  appointmentNotes:
    '<p>Patient attended follow-up with Dr. Johnson. Knee effusion resolved. Recommended 4 additional weeks PT.</p>',
  billableActivities: [
    { type: 'Coordination of Services', description: 'Clinic Attendance', time: 1.2 },
    { type: 'Travel', description: 'Travel round trip', time: 0.8 },
  ],
  workStatusUpdate: {
    workStatus: 'Modified duty',
    restrictions: 'No lifting > 20 lbs',
    effectiveDate: '2026-10-21',
  },
  proposedTasks: [
    {
      title: 'Request PT Authorization',
      dueDate: '2026-10-24',
      dueDateUtc: '2026-10-24T00:00:00.000Z',
      description: 'Submit 4-week PT auth request',
    },
  ],
  nextAppointment: {
    title: 'Orthopedic Follow-up',
    provider: 'Dr. Johnson',
    date: '2026-11-18',
    description: 'Re-evaluation of knee condition',
  },
  contacts: [
    { name: 'Sarah Jenkins', role: 'Medical Assistant', extension: '204', organizationOrProviderName: 'Ortho West' },
  ],
}

const SpeechReviewModal = ({
  show = true,
  onHide,
  onApply,
  caseNumber = null,
  appointmentId = null,
  initialTranscript = '',
  initialEntities = null,
  simulateMicDenied = false,
  allowBrowserSpeechRecognition = process.env.REACT_APP_ALLOW_BROWSER_SPEECH_RECOGNITION === 'true',
  user = null,
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [audioUrl, setAudioUrl] = useState(null)
  const [permissionError, setPermissionError] = useState(() =>
    simulateMicDenied ? 'NotAllowedError: Microphone access blocked' : null,
  )
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [interimTranscript, setInterimTranscript] = useState('')
  const [extractionStatusMessage, setExtractionStatusMessage] = useState(null)

  const [transcript, setTranscript] = useState(
    initialTranscript ||
      "Attended follow-up evaluation with John Doe and Dr. Johnson at Ortho West. Spent 1.2 hours at the clinic, travel was 0.8 hours round trip. Dr. Johnson noted knee effusion has resolved. Cleared John for light duty with no lifting over 20 pounds. Next visit in 4 weeks. I need to get authorization for 4 more weeks of physical therapy. Conferred with Dr. Johnson's medical assistant Sarah Jenkins at extension 204 regarding medical records.",
  )

  const [entities, setEntities] = useState(() => initialEntities || DEFAULT_EXTRACTED_ENTITIES)
  const [notesHtml, setNotesHtml] = useState(
    () => (initialEntities || DEFAULT_EXTRACTED_ENTITIES).appointmentNotes || '',
  )

  // 6 Categorized entity selection states
  const [selectedCategories, setSelectedCategories] = useState({
    notes: true,
    activities: true,
    workStatus: true,
    tasks: true,
    nextAppointment: true,
    contacts: true,
  })

  const mediaRecorderRef = useRef(null)
  const recognitionRef = useRef(null)
  const isRecordingRef = useRef(false)
  const isPausedRef = useRef(false)
  const skipNextStopExtractRef = useRef(false)
  const liveFinalTranscriptRef = useRef('')
  const latestTranscriptRef = useRef(transcript)
  const lastExtractedTranscriptRef = useRef((transcript || '').trim())
  const audioChunksRef = useRef([])
  const streamRef = useRef(null)
  const timerRef = useRef(null)
  const audioPlayerRef = useRef(null)
  const transcriptTextareaRef = useRef(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    latestTranscriptRef.current = transcript
  }, [transcript])

  // Programmatic initial focus (zero JSX autoFocus per AGENTS.md)
  useEffect(() => {
    if (show && transcriptTextareaRef.current) {
      transcriptTextareaRef.current.focus()
    }
  }, [show])

  // Sync initial entities if prop updates
  useEffect(() => {
    if (initialEntities) {
      setEntities(initialEntities)
      if (initialEntities.appointmentNotes) {
        setNotesHtml(initialEntities.appointmentNotes)
      }
    }
  }, [initialEntities])

  // Clean up media streams, speech recognition, and timer on unmount
  useEffect(() => {
    return () => {
      isRecordingRef.current = false
      if (timerRef.current) clearInterval(timerRef.current)
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null
          recognitionRef.current.stop()
        } catch (_e) {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  if (!show) return null

  const formatTimer = secs => {
    const mins = Math.floor(secs / 60)
    const remaining = secs % 60
    return `${mins < 10 ? '0' : ''}${mins}:${remaining < 10 ? '0' : ''}${remaining}`
  }

  // Run extraction from transcript (calls backend API if caseNumber is present, with immediate local clinical parser)
  const runEntityExtraction = async textToExtract => {
    const cleanText = (textToExtract !== undefined ? textToExtract : latestTranscriptRef.current || '').trim()
    if (!cleanText) return null

    setIsAnalyzing(true)
    setExtractionStatusMessage('Extracting clinical entities from dictation...')

    // Immediately compute local structured extraction so UI updates without delay
    const localExtracted = parseClinicalEntitiesFromTranscript(cleanText)
    let resolvedEntities = localExtracted
    setEntities(localExtracted)
    setNotesHtml(localExtracted.appointmentNotes || `<p>${cleanText}</p>`)
    lastExtractedTranscriptRef.current = cleanText

    if (caseNumber) {
      try {
        const response = await sendRequest({
          url: `/api/cases/${caseNumber}/speech/extract`,
          method: 'POST',
          data: { transcript: cleanText, appointmentId },
        })
        if (response && typeof response === 'object') {
          const validWorkStatus =
            response.workStatusUpdate &&
            response.workStatusUpdate.workStatus &&
            !/^(null|none)$/i.test(String(response.workStatusUpdate.workStatus).trim())
              ? response.workStatusUpdate
              : localExtracted.workStatusUpdate

          const validNextAppt =
            response.nextAppointment &&
            response.nextAppointment.title &&
            !/^(null|none)$/i.test(String(response.nextAppointment.title).trim())
              ? response.nextAppointment
              : localExtracted.nextAppointment

          const apiTasks = Array.isArray(response.proposedTasks)
            ? response.proposedTasks.filter(t => t && t.title && String(t.title).trim())
            : []
          const localTasks = Array.isArray(localExtracted.proposedTasks) ? localExtracted.proposedTasks : []
          const combinedTasks = apiTasks.length > 0 ? apiTasks : localTasks

          const normalizedTasks = combinedTasks.map(t => {
            const rawDue = t.dueDate || (t.dueDateUtc ? String(t.dueDateUtc).slice(0, 10) : null)
            const fallbackDue = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10)
            const dueStr = rawDue || fallbackDue
            const rawTitle = String(t.title || '').trim()
            const needsConciseSummary =
              rawTitle.length > 60 ||
              /^(?:attended|spent|i\s+need\s+to|we\s+need\s+to|need\s+to|patient\s+attended)\b/i.test(rawTitle)
            const finalTitle = needsConciseSummary ? summarizeTaskTitle(rawTitle) : rawTitle
            return {
              ...t,
              title: finalTitle || 'Follow-Up Task',
              description: t.description || rawTitle,
              dueDate: dueStr,
              dueDateUtc: t.dueDateUtc || `${dueStr}T00:00:00.000Z`,
            }
          })

          const merged = {
            transcript: cleanText,
            appointmentNotes: response.appointmentNotes || localExtracted.appointmentNotes,
            billableActivities:
              response.billableActivities && response.billableActivities.length > 0
                ? response.billableActivities
                : localExtracted.billableActivities,
            workStatusUpdate: validWorkStatus,
            proposedTasks: normalizedTasks,
            nextAppointment: validNextAppt,
            contacts: response.contacts && response.contacts.length > 0 ? response.contacts : localExtracted.contacts,
          }
          resolvedEntities = merged
          setEntities(merged)
          if (merged.appointmentNotes) {
            setNotesHtml(merged.appointmentNotes)
          }
        }
      } catch (_err) {
        // Local extraction already applied above
      }
    }

    setIsAnalyzing(false)
    setExtractionStatusMessage('Clinical entities extracted from your transcript.')
    return resolvedEntities
  }

  const runAudioBlobExtraction = async audioBlob => {
    if (!caseNumber || !audioBlob || audioBlob.size === 0) return null

    setIsAnalyzing(true)
    setExtractionStatusMessage('Transcribing audio and extracting clinical entities...')
    try {
      const formData = new FormData()
      formData.append('audioFile', audioBlob, 'dictation.webm')
      if (appointmentId) {
        formData.append('appointmentId', String(appointmentId))
      }

      const response = await sendRequest({
        url: `/api/cases/${caseNumber}/speech/extract`,
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': undefined },
      })

      if (response && typeof response === 'object' && response.transcript) {
        const serverTranscript = String(response.transcript).trim()
        setTranscript(serverTranscript)
        latestTranscriptRef.current = serverTranscript
        lastExtractedTranscriptRef.current = serverTranscript
        return await runEntityExtraction(serverTranscript)
      }
      setIsAnalyzing(false)
      setExtractionStatusMessage('Audio recorded. Enter or refine transcript text below to extract entities.')
      return null
    } catch (_err) {
      setIsAnalyzing(false)
      setExtractionStatusMessage(
        'Server-side audio transcription is not configured. Please type or paste your transcript below.',
      )
      return null
    }
  }

  const startSpeechRecognition = () => {
    if (!allowBrowserSpeechRecognition) return
    if (typeof window === 'undefined') return
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return

    try {
      const recognition = new SpeechRecognition()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'

      recognition.onresult = event => {
        let interim = ''
        let finalChunk = ''

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const res = event.results[i]
          if (res.isFinal) {
            finalChunk += `${res[0].transcript} `
          } else {
            interim += res[0].transcript
          }
        }

        if (finalChunk) {
          liveFinalTranscriptRef.current = `${liveFinalTranscriptRef.current} ${finalChunk}`.replace(/\s+/g, ' ').trim()
        }
        setInterimTranscript(interim)

        const combined = `${liveFinalTranscriptRef.current} ${interim}`.replace(/\s+/g, ' ').trim()
        if (combined) {
          setTranscript(combined)
          latestTranscriptRef.current = combined
          // Live-update preview cards as user speaks so cards are never stale
          const liveEntities = parseClinicalEntitiesFromTranscript(combined)
          setEntities(liveEntities)
          setNotesHtml(liveEntities.appointmentNotes || `<p>${combined}</p>`)
        }
      }

      recognition.onerror = () => {
        // Ignore transient no-speech errors while recording
      }

      recognition.onend = () => {
        if (isRecordingRef.current && !isPausedRef.current) {
          try {
            recognition.start()
          } catch (_e) {}
        }
      }

      recognition.start()
      recognitionRef.current = recognition
    } catch (_e) {
      // Browser SpeechRecognition unavailable; MediaRecorder still captures audio
    }
  }

  const startRecording = async () => {
    if (simulateMicDenied) {
      setPermissionError('NotAllowedError: Microphone access blocked')
      return
    }

    try {
      setPermissionError(null)
      setExtractionStatusMessage(null)

      if (!navigator?.mediaDevices?.getUserMedia) {
        isRecordingRef.current = true
        setIsRecording(true)
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Clear previous sample/old transcript and stale sample entities so new dictation replaces them cleanly!
      liveFinalTranscriptRef.current = ''
      latestTranscriptRef.current = ''
      lastExtractedTranscriptRef.current = ''
      setInterimTranscript('')
      setTranscript('')
      const emptyEntities = parseClinicalEntitiesFromTranscript('')
      setEntities(emptyEntities)
      setNotesHtml('')

      // Start live browser Speech-to-Text recognition
      isRecordingRef.current = true
      isPausedRef.current = false
      startSpeechRecognition()

      if (typeof MediaRecorder !== 'undefined') {
        let mimeType = 'audio/webm'
        if (MediaRecorder.isTypeSupported) {
          if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            mimeType = 'audio/webm;codecs=opus'
          } else if (MediaRecorder.isTypeSupported('audio/webm')) {
            mimeType = 'audio/webm'
          } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
            mimeType = 'audio/mp4'
          }
        }

        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
        mediaRecorderRef.current = recorder
        audioChunksRef.current = []

        recorder.ondataavailable = e => {
          if (e.data && e.data.size > 0) {
            audioChunksRef.current.push(e.data)
          }
        }

        recorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' })
          if (typeof URL !== 'undefined' && URL.createObjectURL) {
            const url = URL.createObjectURL(audioBlob)
            setAudioUrl(url)
          }

          if (skipNextStopExtractRef.current) {
            skipNextStopExtractRef.current = false
            return
          }

          // Automatically extract entities from the newly dictated transcript or recorded audio blob
          const spokenText = (latestTranscriptRef.current || liveFinalTranscriptRef.current || '').trim()
          if (spokenText) {
            await runEntityExtraction(spokenText)
          } else if (audioBlob && audioBlob.size > 0) {
            await runAudioBlobExtraction(audioBlob)
          }
        }

        recorder.start(250)
      }

      setIsRecording(true)
      setIsPaused(false)
      setRecordingSeconds(0)

      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1)
      }, 1000)
    } catch (err) {
      setPermissionError('NotAllowedError: Microphone access blocked. Please check browser permissions.')
      isRecordingRef.current = false
      setIsRecording(false)
    }
  }

  const stopRecording = (options = {}) => {
    const { skipAutoExtract = false } = options
    if (skipAutoExtract) {
      skipNextStopExtractRef.current = true
    }
    isRecordingRef.current = false
    isPausedRef.current = false
    setInterimTranscript('')

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null
        recognitionRef.current.stop()
      } catch (_e) {}
      recognitionRef.current = null
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop()
      } catch (_e) {
        // ignore
      }
    } else if (!skipAutoExtract) {
      const spokenText = (latestTranscriptRef.current || liveFinalTranscriptRef.current || '').trim()
      if (spokenText) {
        runEntityExtraction(spokenText)
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    setIsRecording(false)
    setIsPaused(false)
  }

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const togglePause = () => {
    if (!isRecording) return

    const nextPaused = !isPaused
    isPausedRef.current = nextPaused

    if (recognitionRef.current) {
      try {
        if (nextPaused) {
          recognitionRef.current.stop()
        } else {
          recognitionRef.current.start()
        }
      } catch (_e) {}
    }

    if (mediaRecorderRef.current) {
      if (isPaused) {
        mediaRecorderRef.current.resume()
        timerRef.current = setInterval(() => {
          setRecordingSeconds(prev => prev + 1)
        }, 1000)
      } else {
        mediaRecorderRef.current.pause()
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }
    setIsPaused(nextPaused)
  }

  const handleFileUpload = e => {
    const file = e.target.files?.[0]
    if (file) {
      const url = URL.createObjectURL(file)
      setAudioUrl(url)
    }
  }

  const handleActivityTimeChange = (index, delta) => {
    setEntities(prev => {
      const updated = [...(prev.billableActivities || [])]
      if (updated[index]) {
        const current = updated[index].time || 0
        const newTime = Math.max(0.1, Math.round((current + delta) * 10) / 10)
        updated[index] = { ...updated[index], time: newTime }
      }
      return { ...prev, billableActivities: updated }
    })
  }

  const toggleCategory = key => {
    setSelectedCategories(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleTaskTitleChange = (index, newTitle) => {
    setEntities(prev => {
      const updated = [...(prev.proposedTasks || [])]
      if (updated[index]) {
        updated[index] = { ...updated[index], title: newTitle }
      }
      return { ...prev, proposedTasks: updated }
    })
  }

  const handleExtractFromTranscript = async () => {
    if (!transcript.trim()) return
    await runEntityExtraction(transcript)
  }

  const handleClearTranscript = () => {
    liveFinalTranscriptRef.current = ''
    latestTranscriptRef.current = ''
    setInterimTranscript('')
    setTranscript('')
    setExtractionStatusMessage(null)
    if (transcriptTextareaRef.current) {
      transcriptTextareaRef.current.focus()
    }
  }

  const totalBillableHours = (entities.billableActivities || []).reduce(
    (sum, act) => sum + (parseFloat(act.time) || 0),
    0,
  )

  const handleApply = async () => {
    const wasRecording = isRecording || isRecordingRef.current
    if (wasRecording) {
      stopRecording({ skipAutoExtract: true })
    }

    const currentSpokenText = (latestTranscriptRef.current || liveFinalTranscriptRef.current || transcript || '').trim()
    let activeEntities = entities
    let activeNotesHtml = notesHtml

    if (wasRecording || (currentSpokenText && currentSpokenText !== lastExtractedTranscriptRef.current)) {
      const freshlyExtracted = currentSpokenText
        ? await runEntityExtraction(currentSpokenText)
        : parseClinicalEntitiesFromTranscript('')
      if (freshlyExtracted) {
        activeEntities = freshlyExtracted
        activeNotesHtml = freshlyExtracted.appointmentNotes || (currentSpokenText ? `<p>${currentSpokenText}</p>` : '')
      }
    }

    // Sanitize rich-text notes using DOMPurify before committing
    const sanitizedNotes = isQuillEmpty(activeNotesHtml) ? '' : DOMPurify.sanitize(activeNotesHtml)
    const numericCaseNumber = caseNumber ? parseInt(caseNumber, 10) : 0
    const defaultDueStr = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10)

    const preparedTasks = (activeEntities.proposedTasks || []).map(t => {
      const dueStr = t.dueDate || (t.dueDateUtc ? String(t.dueDateUtc).slice(0, 10) : defaultDueStr)
      const rawTitle = String(t.title || '').trim()
      const needsConciseSummary =
        rawTitle.length > 60 ||
        /^(?:attended|spent|i\s+need\s+to|we\s+need\s+to|need\s+to|patient\s+attended)\b/i.test(rawTitle)
      const finalTitle = needsConciseSummary ? summarizeTaskTitle(rawTitle) : rawTitle
      return {
        ...t,
        title: finalTitle || 'Follow-Up Task',
        description: t.description || rawTitle,
        caseNumber: t.caseNumber || numericCaseNumber,
        dueDate: dueStr,
        dueDateUtc: t.dueDateUtc || `${dueStr}T00:00:00.000Z`,
      }
    })

    const preparedActivities = (activeEntities.billableActivities || []).map(act => ({
      ...act,
      caseNumber: act.caseNumber || numericCaseNumber,
    }))

    const finalTranscript = currentSpokenText || transcript || activeEntities.transcript || ''

    const preparedEntities = {
      ...activeEntities,
      transcript: finalTranscript,
      appointmentNotes: sanitizedNotes,
      billableActivities: preparedActivities,
      proposedTasks: preparedTasks,
    }

    let appliedViaApi = false

    if (caseNumber && Permissions.can(user, CAPABILITIES.UPDATE_CASE)) {
      try {
        await sendRequest({
          url: `/api/cases/${caseNumber}/speech/apply`,
          method: 'POST',
          data: {
            applyNotes: selectedCategories.notes,
            applyActivities: selectedCategories.activities,
            applyWorkStatus: selectedCategories.workStatus,
            applyTasks: selectedCategories.tasks,
            applyNextAppointment: selectedCategories.nextAppointment,
            applyContacts: selectedCategories.contacts,
            appointmentId,
            entities: preparedEntities,
          },
        })
        appliedViaApi = true
      } catch (_e) {
        // Continue to onApply callback
      }
    }

    const payload = {
      transcript: finalTranscript,
      selectedCategories,
      appointmentId,
      appliedViaApi,
      entities: preparedEntities,
    }

    if (onApply) {
      onApply(payload)
    }
  }

  return (
    <div
      className="modal show d-block speech-review-modal"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 1050, overflowY: 'auto' }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title font-weight-bold">
              <FAIcon name="microphone" className="mr-2 text-primary" />
              Smart Speech Dictation & HITL Extraction
            </h5>
            <button type="button" className="close" onClick={onHide} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            {/* Permission Warning Banner */}
            {permissionError && (
              <div className="alert alert-warning mic-permission-warning" role="alert">
                <FAIcon name="exclamation-triangle" className="mr-2" />
                <strong>Microphone access blocked.</strong> Please check your browser permissions or paste transcript
                text below.
              </div>
            )}

            {/* Audio Recording & Dictation Controls */}
            <div className="dictation-controls card p-3 mb-3 bg-light border-0">
              <div className="d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
                <button
                  type="button"
                  className={`btn ${isRecording ? 'btn-danger' : 'btn-primary'} btn-record d-flex align-items-center`}
                  onClick={toggleRecording}
                >
                  <FAIcon name={isRecording ? 'stop-circle' : 'microphone'} className="mr-2" />
                  {isRecording ? 'Stop Dictation' : 'Start Dictation'}
                </button>

                {isRecording && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-pause d-flex align-items-center"
                    onClick={togglePause}
                  >
                    <FAIcon name={isPaused ? 'play' : 'pause'} className="mr-2" />
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                )}

                {isRecording && (
                  <span className="badge badge-danger p-2 recording-timer d-flex align-items-center">
                    <span
                      className="recording-pulse mr-2"
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                      }}
                    />
                    {formatTimer(recordingSeconds)} — Listening (speak now)...
                  </span>
                )}

                {/* Upload Audio Fallback */}
                <input
                  type="file"
                  accept="audio/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm upload-audio-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload pre-recorded audio file"
                >
                  <FAIcon name="upload" className="mr-1" /> Upload Audio
                </button>

                {/* HTML5 Audio Player */}
                <div className="ml-auto d-flex align-items-center">
                  <audio
                    ref={audioPlayerRef}
                    controls
                    data-testid="speech-audio-player"
                    className="speech-audio-player"
                    src={audioUrl || undefined}
                  >
                    <track kind="captions" />
                  </audio>
                </div>
              </div>
            </div>

            {/* Editable Transcript Section */}
            <div className="transcript-section card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="transcriptTextarea" className="font-weight-bold mb-0">
                  <FAIcon name="file-alt" className="mr-2 text-secondary" />
                  Spoken Transcript
                  {isRecording && <span className="badge badge-danger ml-2">Live Speech-to-Text Active</span>}
                </label>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm clear-transcript-btn"
                    onClick={handleClearTranscript}
                    disabled={isAnalyzing || !transcript}
                    title="Clear transcript text"
                  >
                    <FAIcon name="eraser" className="mr-1" />
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-info btn-sm re-extract-btn font-weight-bold"
                    onClick={handleExtractFromTranscript}
                    disabled={isAnalyzing || !transcript.trim()}
                  >
                    <FAIcon name="sync" className={`mr-1 ${isAnalyzing ? 'fa-spin' : ''}`} />
                    {isAnalyzing ? 'Extracting...' : 'Re-Extract Entities'}
                  </button>
                </div>
              </div>
              <textarea
                id="transcriptTextarea"
                ref={transcriptTextareaRef}
                className="form-control transcript-textarea"
                rows={3}
                value={transcript}
                onChange={e => {
                  const nextVal = e.target.value
                  setTranscript(nextVal)
                  latestTranscriptRef.current = nextVal
                  const liveEntities = parseClinicalEntitiesFromTranscript(nextVal)
                  setEntities(liveEntities)
                  setNotesHtml(liveEntities.appointmentNotes || (nextVal.trim() ? `<p>${nextVal.trim()}</p>` : ''))
                }}
                placeholder={
                  isRecording
                    ? 'Listening to microphone... Speak your appointment notes, time spent, restrictions, and follow-up tasks...'
                    : 'Click "Start Dictation" to speak, or type/paste your transcript here and click "Re-Extract Entities"...'
                }
              />
              {interimTranscript && isRecording && (
                <small className="text-primary mt-1 d-block font-italic">
                  Hearing: &ldquo;{interimTranscript}&rdquo;
                </small>
              )}
              {extractionStatusMessage && !isRecording && (
                <small className="text-success mt-1 d-block font-weight-bold">
                  <FAIcon name="check-circle" className="mr-1" />
                  {extractionStatusMessage}
                </small>
              )}
            </div>

            {/* 6 Categorized Clinical Review Cards */}
            <div className="categorized-cards-grid row">
              {/* 1. Appointment Notes Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card notes-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.notes}
                        onChange={() => toggleCategory('notes')}
                      />
                      Appointment Notes
                    </label>
                    <span className="badge badge-primary">Clinical Note</span>
                  </div>
                  <div className="card-body">
                    <div
                      className="notes-preview-content p-2 bg-light rounded"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notesHtml) }}
                    />
                  </div>
                </div>
              </div>

              {/* 2. Billable Activities Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card activities-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.activities}
                        onChange={() => toggleCategory('activities')}
                      />
                      Billable Activities ({totalBillableHours.toFixed(1)} hrs)
                    </label>
                    <span className="badge badge-success">{totalBillableHours.toFixed(1)} hrs</span>
                  </div>
                  <div className="card-body">
                    {(entities.billableActivities || []).length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {(entities.billableActivities || []).map((act, i) => (
                          <li
                            key={i}
                            className="mb-2 d-flex justify-content-between align-items-center p-2 bg-light rounded"
                          >
                            <div>
                              <strong>{act.type}</strong>: {act.description}
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm p-0 px-1"
                                onClick={() => handleActivityTimeChange(i, -0.1)}
                                title="Decrease 0.1 hr"
                              >
                                -
                              </button>
                              <span className="badge badge-dark px-2 py-1">{act.time} hr</span>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm p-0 px-1"
                                onClick={() => handleActivityTimeChange(i, 0.1)}
                                title="Increase 0.1 hr"
                              >
                                +
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0">No billable durations detected in transcript</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 3. Work Status Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card work-status-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.workStatus}
                        onChange={() => toggleCategory('workStatus')}
                      />
                      Work Status Update
                    </label>
                    <span className="badge badge-warning">Restrictions</span>
                  </div>
                  <div className="card-body">
                    {entities.workStatusUpdate ? (
                      <p className="mb-0">
                        <strong>Status:</strong> {entities.workStatusUpdate.workStatus}
                        <br />
                        <strong>Restrictions:</strong> {entities.workStatusUpdate.restrictions || 'None stated'}
                        {entities.workStatusUpdate.effectiveDate && (
                          <>
                            <br />
                            <strong>Effective Date:</strong> {entities.workStatusUpdate.effectiveDate}
                          </>
                        )}
                      </p>
                    ) : (
                      <p className="text-muted mb-0">No work status change detected</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Follow-Up Tasks Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card tasks-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.tasks}
                        onChange={() => toggleCategory('tasks')}
                      />
                      Follow-Up Tasks
                    </label>
                    <span className="badge badge-info">{(entities.proposedTasks || []).length} Task(s)</span>
                  </div>
                  <div className="card-body">
                    {(entities.proposedTasks || []).length > 0 ? (
                      entities.proposedTasks.map((t, i) => (
                        <div key={i} className="mb-2 p-2 bg-light rounded">
                          <strong className="d-block mb-1">{t.title}</strong>
                          <input
                            type="text"
                            className="form-control form-control-sm mb-1 proposed-task-title-input"
                            aria-label={`Follow-up task ${i + 1} title`}
                            value={t.title || ''}
                            onChange={e => handleTaskTitleChange(i, e.target.value)}
                          />
                          {t.description && t.description !== t.title && (
                            <small className="text-secondary d-block mb-1">{t.description}</small>
                          )}
                          <small className="text-muted">Due: {t.dueDate || t.dueDateUtc || 'Standard lead-time'}</small>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted mb-0">No tasks inferred</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 5. Next Appointment Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card next-appt-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.nextAppointment}
                        onChange={() => toggleCategory('nextAppointment')}
                      />
                      Next Appointment
                    </label>
                    <span className="badge badge-secondary">Scheduling</span>
                  </div>
                  <div className="card-body">
                    {entities.nextAppointment ? (
                      <p className="mb-0">
                        <strong>{entities.nextAppointment.title}</strong>
                        {entities.nextAppointment.provider && ` with ${entities.nextAppointment.provider}`}
                        {(entities.nextAppointment.date || entities.nextAppointment.suggestedDate) &&
                          ` on ${String(entities.nextAppointment.date || entities.nextAppointment.suggestedDate).slice(
                            0,
                            10,
                          )}`}
                      </p>
                    ) : (
                      <p className="text-muted mb-0">No follow-up visit suggested</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 6. Key Contacts Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card contacts-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.contacts}
                        onChange={() => toggleCategory('contacts')}
                      />
                      Key Contacts
                    </label>
                    <span className="badge badge-dark">{(entities.contacts || []).length} Contact(s)</span>
                  </div>
                  <div className="card-body">
                    {(entities.contacts || []).length > 0 ? (
                      entities.contacts.map((c, i) => (
                        <p key={i} className="mb-1 p-2 bg-light rounded">
                          <strong>{c.name}</strong> ({c.role}
                          {c.extension ? `, ext. ${c.extension}` : ''}
                          {c.organizationOrProviderName ? ` - ${c.organizationOrProviderName}` : ''})
                        </p>
                      ))
                    ) : (
                      <p className="text-muted mb-0">No contacts mentioned</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer d-flex justify-content-between align-items-center">
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Cancel
            </button>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              {isRecording && (
                <button
                  type="button"
                  className="btn btn-danger stop-and-review-btn d-flex align-items-center font-weight-bold"
                  onClick={() => stopRecording()}
                >
                  <FAIcon name="stop-circle" className="mr-2" />
                  Stop Dictation &amp; Review
                </button>
              )}
              <button
                type="button"
                className="btn btn-success apply-to-case-btn d-flex align-items-center font-weight-bold"
                onClick={handleApply}
                disabled={isAnalyzing}
              >
                <FAIcon name={isAnalyzing ? 'sync' : 'check'} className={`mr-2 ${isAnalyzing ? 'fa-spin' : ''}`} />
                {isAnalyzing ? 'Extracting & Applying...' : isRecording ? 'Stop & Apply to Case' : 'Apply to Case'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SpeechReviewModal
